/**
 * GitHub Service
 * 
 * Handles API communication for GitHub project import feature
 * Provides methods for browsing repos, importing, syncing, and disconnecting
 */

import api from './api';

export interface GitHubRepository {
  id: number;
  project_id: number;
  user_id: number;
  github_id: number;
  owner: string;
  name: string;
  full_name: string;
  default_branch: string;
  description?: string;
  url: string;
  clone_url: string;
  is_private: boolean;
  access_token?: string; // Not returned from API
  last_synced_at?: string;
  sync_status: 'pending' | 'syncing' | 'synced' | 'error';
  sync_error?: string;
  webhook_id?: string;
  webhook_secret?: string;
  webhook_active: boolean;
  stars_count: number;
  forks_count: number;
  watchers_count: number;
  open_issues_count: number;
  auto_sync: boolean;
  sync_paths?: any;
  metadata?: any;
  created_at: string;
  updated_at: string;
  deleted_at?: string;

  // Relationships
  project?: {
    id: number;
    name: string;
  };
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface GitHubRepoData {
  id: number;
  name: string;
  full_name: string;
  owner: {
    login: string;
    avatar_url: string;
  };
  description?: string;
  html_url: string;
  clone_url: string;
  private: boolean;
  default_branch: string;
  stargazers_count: number;
  forks_count: number;
  watchers_count: number;
  open_issues_count: number;
  language?: string;
  updated_at: string;
  created_at: string;
  pushed_at: string;
}

export interface SpecsImportResult {
  success: boolean;
  message: string;
  data?: {
    tasks_created?: number;
    tasks_completed?: number;
    has_plan?: boolean;
    has_tasks?: boolean;
    fallback_task?: boolean;
    task_id?: number;
    task_title?: string;
    assigned_to?: string;
    // New fields for queued import
    spec_folder?: string;
    files_found?: {
      plan?: string | null;
      tasks?: string | null;
      specification?: string | null;
      constitution?: string | null;
    };
    import_status?: 'queued' | 'processing' | 'completed' | 'failed';
    queued_at?: string;
  };
}

export interface ImportRepositoryResponse {
  success: boolean;
  message: string;
  repository: GitHubRepository;
  specs_import?: SpecsImportResult;
}

class GitHubService {
  /**
   * Get user's GitHub repositories
   * Requires GitHub access token from OAuth flow
   */
  async getUserRepositories(projectId: number, accessToken: string): Promise<GitHubRepoData[]> {
    try {
      const response = await api.get(`/admin/projects/${projectId}/github/repositories`, {
        params: { access_token: accessToken },
      });

      if (response.data.success) {
        return response.data.repositories;
      }

      throw new Error(response.data.message || 'Failed to fetch repositories');
    } catch (error: any) {
      console.error('Failed to get GitHub repositories:', error);
      throw error;
    }
  }

  /**
   * Import a GitHub repository to a project
   */
  async importRepository(
    projectId: number,
    accessToken: string,
    repoData: GitHubRepoData
  ): Promise<ImportRepositoryResponse> {
    try {
      const response = await api.post(`/admin/projects/${projectId}/github/import`, {
        access_token: accessToken,
        repository_id: repoData.id,
        owner: repoData.owner.login,
        name: repoData.name,
        full_name: repoData.full_name,
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          repository: response.data.repository,
          specs_import: response.data.specs_import,
        };
      }

      throw new Error(response.data.message || 'Failed to import repository');
    } catch (error: any) {
      console.error('Failed to import repository:', error);
      throw error;
    }
  }

  /**
   * Import specs from connected GitHub repository
   */
  async importSpecs(projectId: number): Promise<SpecsImportResult> {
    try {
      const response = await api.post(`/admin/projects/${projectId}/github/import-specs`);

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.message || 'Failed to import specs');
    } catch (error: any) {
      console.error('Failed to import specs:', error);
      throw error;
    }
  }

  /**
   * Get project's connected GitHub repository
   */
  async getProjectRepository(projectId: number): Promise<GitHubRepository | null> {
    try {
      const response = await api.get(`/admin/projects/${projectId}/github/repository`);

      if (response.data.success) {
        return response.data.repository;
      }

      // Not found is expected for projects without repos
      if (response.status === 404) {
        return null;
      }

      throw new Error(response.data.message || 'Failed to fetch repository');
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get project repository:', error);
      throw error;
    }
  }

  /**
   * Manually sync repository from GitHub
   */
  async syncRepository(projectId: number): Promise<GitHubRepository> {
    try {
      const response = await api.post(`/admin/projects/${projectId}/github/sync`);

      if (response.data.success) {
        return response.data.repository;
      }

      throw new Error(response.data.message || 'Failed to sync repository');
    } catch (error: any) {
      console.error('Failed to sync repository:', error);
      throw error;
    }
  }

  /**
   * Disconnect repository from project
   */
  async disconnectRepository(projectId: number): Promise<void> {
    try {
      const response = await api.delete(`/admin/projects/${projectId}/github/disconnect`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to disconnect repository');
      }
    } catch (error: any) {
      console.error('Failed to disconnect repository:', error);
      throw error;
    }
  }

  /**
   * Get GitHub OAuth authorization URL
   * This would typically be handled by the backend, but included for reference
   */
  getAuthorizationUrl(state: string): string {
    // This would be fetched from backend or constructed based on config
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/api/v1/auth/github/callback`;
    const scope = 'repo,read:user,write:repo_hook';

    return `https://github.com/login/oauth/authorize?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${scope}&` +
      `state=${state}`;
  }

  /**
   * Format sync status for display
   */
  formatSyncStatus(status: GitHubRepository['sync_status']): {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  } {
    switch (status) {
      case 'pending':
        return { label: 'Pending', color: 'default' };
      case 'syncing':
        return { label: 'Syncing...', color: 'primary' };
      case 'synced':
        return { label: 'Synced', color: 'success' };
      case 'error':
        return { label: 'Error', color: 'error' };
      default:
        return { label: 'Unknown', color: 'default' };
    }
  }

  /**
   * Get relative time string (e.g., "2 hours ago")
   */
  getRelativeTime(dateString?: string): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;

    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  }
}

export default new GitHubService();
